import { findFirstNonEmptyString } from "@product/scmp-sdk";
import { differenceInDays } from "date-fns";
import first from "lodash/first";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useImageUtils } from "~/lib/hooks";
import { formatInTimeZone, parseDate } from "~/lib/utils";
import type { bannerSeriesArticleArticle$key } from "~/queries/__generated__/bannerSeriesArticleArticle.graphql";

import {
  Bookmark,
  Container,
  Content,
  Headline,
  NewTag,
  StyledBaseLink,
  StyledContentBookmark,
  StyledContentShareWidget,
  StyledSeriesArticleSectionTag,
  Summary,
  Time,
  Toolbar,
  Updated,
} from "./styles";

export type Props = {
  className?: string;
  reference: bannerSeriesArticleArticle$key;
};

export const SeriesArticleBanner = forwardRef<HTMLDivElement, Props>(
  ({ className, reference }, htmlReference) => {
    const { getResizedImageUrl } = useImageUtils();

    const queueItem = useFragment(
      graphql`
        fragment bannerSeriesArticleArticle on Article {
          ...sectionTagSeriesArticleArticle
          ...contentShareWidgetContent
          entityId
          headline
          socialHeadline
          summary {
            text
          }
          updatedDate
          publishedDate
          images {
            isSlideshow
            url
            size768x768: style(filter: { style: "768x768" }) {
              url
            }
            size800x1200: style(filter: { style: "800x1200" }) {
              url
            }
            sizeLandscape250x99: style(filter: { style: "landscape_250_99" }) {
              url
            }
          }
          coverDesktop: image(filter: { type: COVER }) {
            url
            sizeLandscape250x99: style(filter: { style: "landscape_250_99" }) {
              url
            }
          }
          coverMobile: image(filter: { type: COVER_MOBILE }) {
            url
          }
        }
      `,
      reference,
    );

    const renderImageSources = () => {
      const slideShowImage = first(queueItem.images?.filter(image => image?.isSlideshow));
      const defaultImageUrl =
        "data:image/gif;base64,iVBORw0KGgoAAAANSUhEUgAAAAMAAAACCAQAAAA3fa6RAAAAEElEQVR42mN8+p8BCBghFAAiEQPLD3901QAAAABJRU5ErkJggg==";

      const variantSpecs = {
        default: {
          url: findFirstNonEmptyString(
            queueItem.coverMobile?.url,
            slideShowImage?.size800x1200?.url,
            defaultImageUrl,
          ),
          width: 425,
        },
        desktopUp: {
          url: findFirstNonEmptyString(
            queueItem.coverDesktop?.sizeLandscape250x99?.url,
            slideShowImage?.sizeLandscape250x99?.url,
            defaultImageUrl,
          ),
          width: 2000,
        },
        tabletUp: {
          url: findFirstNonEmptyString(
            queueItem.coverDesktop?.url,
            slideShowImage?.size768x768?.url,
            defaultImageUrl,
          ),
          width: 768,
        },
      };
      return {
        $default: getResizedImageUrl(variantSpecs.default.url ?? "", variantSpecs.default.width),
        $desktopUp: getResizedImageUrl(
          variantSpecs.desktopUp.url ?? "",
          variantSpecs.desktopUp.width,
        ),
        $tabletUp: getResizedImageUrl(variantSpecs.tabletUp.url ?? "", variantSpecs.tabletUp.width),
      };
    };

    const renderHeadline = () =>
      findFirstNonEmptyString(queueItem.socialHeadline, queueItem.headline);

    const isShowNewSeries = () => {
      if (!queueItem?.publishedDate) return false;
      const diff = differenceInDays(new Date(), parseDate(queueItem.publishedDate));
      return diff <= 30;
    };

    return (
      <Container $backgroundImages={renderImageSources()} className={className} ref={htmlReference}>
        <Content>
          <StyledBaseLink as="/series" pathname="/series">
            {isShowNewSeries() && <NewTag>New</NewTag>}
            <StyledSeriesArticleSectionTag reference={queueItem} />
          </StyledBaseLink>
          <Headline>{renderHeadline()}</Headline>
          <Summary>{queueItem?.summary?.text}</Summary>
          <Updated>
            {queueItem?.updatedDate && (
              <Time>Updated: {formatInTimeZone(queueItem.updatedDate, "d MMM yyyy")}</Time>
            )}
            <Toolbar>
              <StyledContentShareWidget
                extraProps={{
                  copyLink: {
                    disablePortal: false,
                    tooltipPlacement: "bottom",
                    variant: "tooltip",
                  },
                }}
                position="article_header"
                reference={queueItem}
                style={{
                  backgroundColor: "transparent",
                }}
                types={["facebook", "twitter", "copy-link", "email"]}
              />
              <Bookmark>
                <StyledContentBookmark
                  entityId={queueItem.entityId}
                  ga4Events={{
                    click: {
                      action: "click",
                      category: "bookmark",
                      customized_parameters: {
                        action_type: "add",
                        article_id: queueItem.entityId,
                        trigger_point: "rail",
                      },
                      subcategory: "",
                    },
                  }}
                  key="content-bookmark"
                  location="side"
                />
              </Bookmark>
            </Toolbar>
          </Updated>
        </Content>
      </Container>
    );
  },
);

SeriesArticleBanner.displayName = "SeriesArticleBanner";
